import { nonNullable, take, uniqueBy } from 'lib/array/arrayUtils'
import type { LoyaltyEarnTotals } from 'loyalty/hooks/useGroupedLoyaltyEarnTotals'
import {
  buildDisplayedPriceSummary,
  displayContextBody,
  displayContextField,
  formatOfferPageLoyaltyEarnSummary,
} from './offerPageContextFormatting'
import type {
  AccommodationOfferPageAssistantRoomPackageSummaryRequest,
  BuildOfferPageSurfaceContextProps,
  OfferPageAssistantPriceFormatContext,
  PropertyRoomRateViewForAssistant,
} from './offerPageContextTypes'
import { getBedbankMemberTotalIncludingPropertyFees } from './offerPageBedbankPricing'

const ROOM_PACKAGE_SUMMARY_LIMIT = 8
const SUMMARY_INCLUSION_LIMIT = 5

function buildRoomPackageSummaryKey(
  summary: AccommodationOfferPageAssistantRoomPackageSummaryRequest,
): string {
  return [
    summary.roomName ?? '',
    summary.packageName ?? '',
    summary.description ?? '',
    summary.priceSummary ?? '',
    summary.loyaltyEarnSummary ?? '',
    summary.inclusions?.join('|') ?? '',
  ].join('::')
}

function inclusionDescriptions(inclusions: Array<App.OfferInclusion> | undefined): Array<string> | undefined {
  const descriptions = nonNullable(
    take(inclusions ?? [], SUMMARY_INCLUSION_LIMIT)
      .map(inclusion => displayContextField(inclusion.description)),
  )

  return descriptions.length ? descriptions : undefined
}

function packageInclusionDescriptions(pkg: App.HotelPackage): Array<string> | undefined {
  const inclusions = [
    ...(pkg.inclusions ?? []),
    ...pkg.bonusInclusions,
  ]
  return inclusionDescriptions(inclusions)
}

function buildHotelPackageSummary(
  packageView: App.OfferPackageOptionView,
  priceFormatContext: OfferPageAssistantPriceFormatContext,
  loyaltyEarnTotals: LoyaltyEarnTotals | undefined,
): AccommodationOfferPageAssistantRoomPackageSummaryRequest {
  return {
    roomName: displayContextField(packageView.package.roomType.name),
    packageName: displayContextField(packageView.package.name),
    description: displayContextBody(packageView.package.description),
    inclusions: packageInclusionDescriptions(packageView.package),
    priceSummary: buildDisplayedPriceSummary({
      memberTotal: packageView.hotelMemberTotal,
      standardTotal: packageView.hotelTotal,
      valuedUpTo: packageView.hotelValue,
      showValuedUpTo: packageView.package.shouldDisplayValue,
    }, priceFormatContext),
    loyaltyEarnSummary: displayContextField(formatOfferPageLoyaltyEarnSummary(loyaltyEarnTotals)),
  }
}

function buildBedbankPackageSummary(
  packageView: App.BedbankOfferPackageView,
  priceFormatContext: OfferPageAssistantPriceFormatContext,
  loyaltyEarnTotals: LoyaltyEarnTotals | undefined,
): AccommodationOfferPageAssistantRoomPackageSummaryRequest {
  const rate = packageView.cheapestRate ?? packageView.effectiveRates[0]

  return {
    roomName: displayContextField(packageView.package.name),
    description: displayContextBody(packageView.package.description),
    inclusions: inclusionDescriptions(rate?.inclusions),
    priceSummary: buildDisplayedPriceSummary({
      memberTotal: rate ? getBedbankMemberTotalIncludingPropertyFees(rate) : undefined,
      standardTotal: rate?.totals.total ?? packageView.package.sell?.price,
      valuedUpTo: rate?.value ?? packageView.package.sell?.value,
    }, priceFormatContext),
    loyaltyEarnSummary: displayContextField(formatOfferPageLoyaltyEarnSummary(loyaltyEarnTotals)),
  }
}

function buildPropertyRoomRateSummary(
  roomRateView: PropertyRoomRateViewForAssistant,
  priceFormatContext: OfferPageAssistantPriceFormatContext,
  loyaltyEarnTotals: LoyaltyEarnTotals | undefined,
): AccommodationOfferPageAssistantRoomPackageSummaryRequest {
  const rate = roomRateView.rates[0]
  return {
    roomName: displayContextField(roomRateView.propertyRoom.name),
    packageName: displayContextField(rate?.name),
    description: displayContextBody(roomRateView.propertyRoom.description),
    inclusions: inclusionDescriptions(rate?.inclusions ?? roomRateView.propertyRoom.inclusions),
    priceSummary: buildDisplayedPriceSummary({
      memberTotal: rate?.luxPlusTotals?.displayPrice,
      standardTotal: rate?.totals.displayPrice,
      valuedUpTo: rate?.totals.valuedUpToPrice,
    }, priceFormatContext),
    loyaltyEarnSummary: displayContextField(formatOfferPageLoyaltyEarnSummary(loyaltyEarnTotals)),
  }
}

export function buildRoomPackageSummaries({
  hotelPackageViews = [],
  bedbankPackageViews = [],
  propertyRoomRateViews = [],
  roomPackageLoyaltyEarnTotals = [],
  priceFormatContext,
}: Pick<BuildOfferPageSurfaceContextProps, 'hotelPackageViews' | 'bedbankPackageViews' | 'propertyRoomRateViews' | 'roomPackageLoyaltyEarnTotals' | 'priceFormatContext'>): Array<AccommodationOfferPageAssistantRoomPackageSummaryRequest> {
  const propertySummaries = uniqueBy(
    propertyRoomRateViews.map((roomRateView, index) => buildPropertyRoomRateSummary(roomRateView, priceFormatContext, roomPackageLoyaltyEarnTotals[index])),
    buildRoomPackageSummaryKey,
  )
  if (propertySummaries.length) {
    return take(propertySummaries, ROOM_PACKAGE_SUMMARY_LIMIT)
  }

  const hotelSummaries = uniqueBy(
    hotelPackageViews.map((packageView, index) => buildHotelPackageSummary(packageView, priceFormatContext, roomPackageLoyaltyEarnTotals[index])),
    buildRoomPackageSummaryKey,
  )
  if (hotelSummaries.length) {
    return take(hotelSummaries, ROOM_PACKAGE_SUMMARY_LIMIT)
  }

  return take(
    uniqueBy(
      bedbankPackageViews.map((packageView, index) => buildBedbankPackageSummary(packageView, priceFormatContext, roomPackageLoyaltyEarnTotals[index])),
      buildRoomPackageSummaryKey,
    ),
    ROOM_PACKAGE_SUMMARY_LIMIT,
  )
}
